import { ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal, Chapter } from "@/components/Reveal";
import { InflectionCurve } from "@/components/Curve";
import { Marquee } from "@/components/Marquee";
import { CTASection } from "@/components/CTASection";
import { SITE } from "@/siteConfig";

const DIFFERENT = [
  {
    title: "Executive coaching grounded in real operating experience",
    desc: "Not theory — coaching from someone who has sat in the seat.",
  },
  {
    title: "Hands-on operational leadership (Fractional COO)",
    desc: "We don't just advise, we help run it.",
  },
  {
    title: "Portfolio and PMO expertise",
    desc: "So transformation actually gets delivered, not just designed.",
  },
  {
    title: "AI transformation capability",
    desc: "From strategy through workflow redesign.",
  },
  {
    title: "Change management and staffing strategy",
    desc: "Because technology only works if your people and structure support it.",
  },
];

const WHO = [
  "CEOs, Presidents & Vice Presidents",
  "New and transitioning executives",
  "Military leaders transitioning to civilian leadership",
  "Women in executive roles",
  "High-potential leaders",
  "Companies with 20–250 employees",
  "Technology, professional services, engineering & defence",
];

export default function About() {
  return (
    <main data-testid="about-page">
      {/* HEADER */}
      <section className="relative overflow-hidden pt-40 pb-20 md:pt-48 md:pb-28">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 45% at 80% 0%, rgba(43,217,199,0.08), transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10">
          <Reveal>
            <p className="overline-label mb-8">About QuInflection</p>
          </Reveal>
          <h1
            className="max-w-4xl font-serif text-4xl leading-[1.02] tracking-tighter text-white sm:text-5xl md:text-7xl"
            data-testid="about-headline"
          >
            <MaskedLines
              lines={[
                "Built by Someone",
                <span key="b" className="italic text-teal">Who's Been There</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
              Most coaches only coach. Most consultants only advise. QuInflection combines
              executive coaching, operational leadership, large-scale transformation,
              organizational change, portfolio management, AI strategy, and workforce planning
              into one partnership — built for leaders facing real inflection points.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <img
              src="/logo-horizontal.png"
              alt="QuInflection Advisory — Own Your Turning Point"
              className="mt-12 h-auto w-full max-w-lg"
              data-testid="about-brand-logo"
            />
          </Reveal>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="border-t border-white/10 bg-navy-deep" data-testid="vision-mission-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Chapter index="01" label="Vision & Mission" />
          <div className="mt-14 grid gap-16 md:grid-cols-2">
            <Reveal>
              <div className="border-l-2 border-teal pl-8">
                <p className="overline-label mb-4">Vision</p>
                <p className="font-serif text-xl leading-relaxed text-white sm:text-2xl">
                  To become Canada's premier executive advisory firm — helping organizations
                  navigate AI-driven transformation, build the workforce strategies to sustain
                  it, and develop exceptional leaders along the way.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="border-l-2 border-white/20 pl-8">
                <p className="overline-label mb-4">Mission</p>
                <p className="font-serif text-xl leading-relaxed text-white/80 sm:text-2xl">
                  Helping leaders create clarity, build capability, and deliver transformation —
                  across strategy, operations, technology, and people.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="border-t border-white/10" data-testid="founder-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Chapter index="02" label="Our Story" />
          <div className="mt-14 grid gap-16 md:grid-cols-12">
            <div className="md:col-span-4">
              <Reveal>
                <div className="relative overflow-visible border border-white/10 bg-navy-deep p-10" data-testid="founder-card">
                  <InflectionCurve className="absolute -top-10 right-4 w-32 opacity-50" animate={false} />
                  <div className="relative h-40 w-40 overflow-hidden rounded-full border border-teal/40">
                    <img
                      src={SITE.headshotUrl}
                      alt="Karrie Quinn, Owner & Founder of QuInflection Advisory"
                      className="h-full w-full object-cover"
                      data-testid="founder-headshot"
                    />
                  </div>
                  <p className="mt-8 font-serif text-2xl text-white">{SITE.founder}</p>
                  <p className="mt-2 font-sans text-xs uppercase tracking-[0.25em] text-white/50">
                    {SITE.founderRole}
                  </p>
                  <p className="mt-6 font-sans text-sm leading-relaxed text-white/50">
                    20+ years of technology and operations leadership in highly regulated,
                    mission-critical environments.
                  </p>
                  <a
                    href={SITE.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="founder-linkedin"
                    className="group mt-8 inline-flex items-center gap-2 font-sans text-sm tracking-wide text-white/60 transition-colors duration-300 hover:text-teal"
                  >
                    Connect on LinkedIn
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </a>
                </div>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <Reveal>
                <p className="font-serif text-2xl leading-snug text-white sm:text-3xl">
                  QuInflection was founded on a simple observation: leaders facing turning points
                  shouldn't need five different advisors to get through one.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-8 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg" data-testid="founder-bio-1">
                  As a senior technology and operations executive with more than 20 years of
                  leadership experience, Karrie builds and leads high-performing organizations
                  delivering complex software, engineering and technology programs in highly
                  regulated, mission-critical environments.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg" data-testid="founder-bio-2">
                  Her career spans telecommunications, defence, aviation, software engineering,
                  and advanced technology — with expertise in enterprise portfolio management,
                  digital transformation, operational excellence, organizational leadership, and
                  large-scale program delivery. She has led multidisciplinary teams, established
                  enterprise governance frameworks, optimized delivery organizations, and
                  partnered with executive leadership and customers to achieve strategic business
                  outcomes.
                </p>
              </Reveal>
              <Reveal delay={0.28}>
                <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
                  QuInflection brings that experience to leaders facing their own inflection
                  points — one partner, one relationship, one team accountable for outcomes.
                </p>
              </Reveal>
              <Reveal delay={0.36}>
                <div className="mt-12">
                  <p className="overline-label mb-5">Industries</p>
                  <div className="flex flex-wrap gap-3" data-testid="founder-industries">
                    {["Telecommunications", "Defence", "Aviation", "Software Engineering", "Advanced Technology"].map((tag) => (
                      <span key={tag} className="rounded-full border border-white/15 px-4 py-2 font-sans text-xs text-white/60">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="overline-label mb-5 mt-8">Expertise</p>
                  <div className="flex flex-wrap gap-3" data-testid="founder-expertise">
                    {["Enterprise Portfolio Management", "Digital Transformation", "Operational Excellence", "Organizational Leadership", "Large-Scale Program Delivery"].map((tag) => (
                      <span key={tag} className="rounded-full border border-teal/30 px-4 py-2 font-sans text-xs text-teal/90">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT MAKES US DIFFERENT */}
      <section className="border-t border-white/10 bg-navy-deep" data-testid="different-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Chapter index="03" label="What Makes Us Different" />
          <Reveal delay={0.1}>
            <h2 className="mt-10 max-w-3xl font-serif text-3xl leading-tight text-white sm:text-4xl">
              Most consulting firms specialize narrowly.{" "}
              <span className="italic text-teal">QuInflection was built differently.</span>
            </h2>
          </Reveal>
          <div className="mt-16 divide-y divide-white/10 border-y border-white/10">
            {DIFFERENT.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.06} y={18}>
                <div className="group grid gap-2 py-7 md:grid-cols-12 md:gap-8" data-testid={`different-${i}`}>
                  <span className="font-sans text-xs tracking-[0.3em] text-teal md:col-span-1">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-serif text-xl text-white transition-transform duration-300 group-hover:translate-x-2 sm:text-2xl md:col-span-6">
                    {d.title}
                  </h3>
                  <p className="font-sans text-sm leading-relaxed text-white/50 md:col-span-5">
                    {d.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl font-sans text-base leading-relaxed text-white/60 md:text-lg">
              This combination is rare in the market. It means one partner, one relationship,
              and one team accountable for outcomes — not a handoff between specialists.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="border-t border-white/10" data-testid="who-section">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
          <Chapter index="04" label="Who We Work With" />
          <div className="mt-14 flex flex-wrap gap-3">
            {WHO.map((w, i) => (
              <Reveal key={w} delay={i * 0.05} y={14}>
                <span
                  className="inline-block rounded-full border border-white/15 px-5 py-2.5 font-sans text-sm text-white/70 transition-colors duration-300 hover:border-teal hover:text-teal"
                  data-testid={`who-chip-${i}`}
                >
                  {w}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      <CTASection testid="about-cta" headline="Let's Talk About Your Turning Point" body="" />
    </main>
  );
}
